import { Component, OnDestroy, OnInit } from '@angular/core';
import { Option } from '../../../core/models/option.model';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import { B2BUserRole } from '../../../core/enums/b2bUserRoles';
import { ReportsScreen } from './_core/models/reports.model';
import { initialStatus, successStatus } from '../../../core/models/request-status.model';
import { Alert, ALERT_TYPE } from '../../../components/alert/alert.model';
import { TranslationService } from '@spartacus/core';

enum REPORTS {
  PRICING_STATUS = 'PRICING_STATUS',
  PURCHASE_SUMMARY = 'PURCHASE_SUMMARY',
  CREDIT_SUMMARY = 'CREDIT_SUMMARY',
  PAYMENT_HISTORY = 'PAYMENT_HISTORY',
}

@Component({
  selector: 'reports',
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
  standalone: false,
})
export class ReportsComponent implements OnInit, OnDestroy {
  REPORTS = REPORTS;
  reqReports$ = new BehaviorSubject<ReportsScreen>(initialStatus);
  currentReport$ = new BehaviorSubject<Option | undefined>(undefined);
  subscription = new Subscription();

  rolesToPricingStatus = [
    B2BUserRole.ADMIN,
    B2BUserRole.SALESREP,
    B2BUserRole.WEBSUPPORT,
    B2BUserRole.ACCPAYABLE,
    B2BUserRole.BUYER,
  ];

  rolesToPurchaseSummary = [
    B2BUserRole.ADMIN,
    B2BUserRole.SALESREP,
    B2BUserRole.WEBSUPPORT,
    B2BUserRole.ACCPAYABLE,
    B2BUserRole.BUYER,
  ];

  rolesToCreditSummary = [B2BUserRole.ADMIN, B2BUserRole.SALESREP, B2BUserRole.WEBSUPPORT, B2BUserRole.ACCPAYABLE];
  rolesToPaymentHistory = [B2BUserRole.ADMIN, B2BUserRole.SALESREP, B2BUserRole.WEBSUPPORT, B2BUserRole.ACCPAYABLE];

  alertMessage: Alert = {
    isActive: true,
    content: '',
    hasCloseButton: false,
    type: ALERT_TYPE.DANGER,
  };

  constructor(
    private userAccountFacade: UserAccountFacade,
    private translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    this.createReports();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async createReports() {
    const textError = await firstValueFrom(this.translationService.translate('generic.error'));
    this.alertMessage.content = textError;

    setTimeout(() => {
      this.subscription.add(
        this.userAccountFacade.get().subscribe(async user => {
          const reports: Option[] = [];
          const userRoles: string[] = user?.roles || [];

          if ((userRoles || []).some(role => this.rolesToPricingStatus.includes(role as B2BUserRole))) {
            const pricingStatusLabel = await firstValueFrom(
              this.translationService.translate('reports.programStatus.pricingStatus'),
            );
            reports.push({
              code: REPORTS.PRICING_STATUS,
              label: pricingStatusLabel,
            });
          }

          if ((userRoles || []).some(role => this.rolesToPurchaseSummary.includes(role as B2BUserRole))) {
            const purchaseSummaryLabel = await firstValueFrom(
              this.translationService.translate('reports.programStatus.purchaseSummary'),
            );
            reports.push({
              code: REPORTS.PURCHASE_SUMMARY,
              label: purchaseSummaryLabel,
            });
          }

          if ((userRoles || []).some(role => this.rolesToCreditSummary.includes(role as B2BUserRole))) {
            const creditSummaryLabel = await firstValueFrom(
              this.translationService.translate('reports.programStatus.creditSummary'),
            );
            reports.push({
              code: REPORTS.CREDIT_SUMMARY,
              label: creditSummaryLabel,
            });
          }

          if ((userRoles || []).some(role => this.rolesToPaymentHistory.includes(role as B2BUserRole))) {
            const paymentHistoryLabel = await firstValueFrom(
              this.translationService.translate('reports.programStatus.paymentHistory'),
            );
            reports.push({
              code: REPORTS.PAYMENT_HISTORY,
              label: paymentHistoryLabel,
            });
          }

          this.reqReports$.next({
            ...successStatus,
            reports,
          });

          this.currentReport$.next(reports[0]);
        }),
      );
    }, 500);
  }
}
